import {Delete, Add, ExpandMore, ExpandLess, WrapText, Visibility, VisibilityOff} from "@mui/icons-material";
import { Typography, Box, Divider, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Collapse, List, ListItem, alpha } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useContext, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { selectModeData, selectProject } from "../../redux/selectors/editor";
import { selectConstants, selectSheets, selectUser } from "../../redux/selectors/firebase";
import { PathwayUser } from "../../redux/types/firebase";
import { ConfirmContext } from "../../utility/ConfirmProvider";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { Constants } from "../graph/StateTags";
import { Form } from "../input/Form";
import { TextFieldAuto } from "../input/TextFieldAuto";
import { autoColDefs, getColumnDetails, ColumnData } from "./gridColumnDefs";
import { Sheet, Sheets } from "./GridView";
import {ISheetModeData} from "../../redux/slices/editor";

const useStyles = makeStyles((theme) => ({
    container: {
        width: "100%"
    },
    toolbar: {
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
    },
    icon: {
        color: theme.palette.primary.contrastText,
    },
    title: {
        marginLeft: theme.spacing(2)
    },
    formControl: {
        boxSizing: "border-box",
        width: "100%",
        padding: theme.spacing(1)
    },
    label: {
        left: "unset",
        top: "unset",
        backgroundColor: "white"
    },
    flexRow: {
        display: "flex",
        flexDirection: "row",
        width: "100%"
    },
    flexColumn: {
        display: "flex",
        flexDirection: "column",
        width: "100%"
    },
    nameText: {
        paddingLeft: theme.spacing(1)
    }
}));

const useRowStyles = makeStyles((theme) => ({
    list: {
        maxHeight: "300px",
        overflowY: "auto",
        width: "100%",
        "& .MuiListItem-root": {
            padding: theme.spacing(0),
            "& div": {
                margin: 1
            }
        }
    },
    wrap: {
        backgroundColor: alpha(theme.palette.common.black, 0.1)
    },
    flexRow: {
        display: "flex",
        flexDirection: "row",
        width: "100%"
    },
    nameText: {
        paddingLeft: theme.spacing(1)
    }
}));

interface RowProps
{
    project: string
    sheetId?: string
    columnEntry: [string, ColumnData];
    handleDeleteColumn: (event: React.MouseEvent<HTMLElement>) => void
    constants: Constants
}

function Row({ project, sheetId, columnEntry, constants, handleDeleteColumn }: RowProps)
{
    const classes = useRowStyles();
    const detail = getColumnDetails(columnEntry, constants);
    const { push, set } = useContext(FirebaseContext);
    const [open, setOpen] = useState(false);
    const [columnId, column] = columnEntry;

    const handleAddValueOption = (newValue: any) =>
    {
        if (sheetId && newValue)
        {
            push(`sheets/${project}/${sheetId}/columns/${columnId}/valueOptions`, newValue);
        }
    }

    const handleSetValueOption = (id: string, value: any) =>
    {
        if (sheetId && value)
        {
            set(`sheets/${project}/${sheetId}/columns/${columnId}/valueOptions/${id}`, value)
        }
    }

    const wrap = column.textWrap ?? false;
    const handleToggleTextWrap = () =>
    {
        if (sheetId)
        {
            set(`sheets/${project}/${sheetId}/columns/${columnId}/textWrap`, !wrap).catch(console.error);
        }
    }

    const hidden = column.hidden ?? false;
    const handleToggleHidden = () =>
    {
        if (sheetId)
        {
            set(`sheets/${project}/${sheetId}/columns/${columnId}/hidden`, !hidden).catch(console.error);
        }
    }

    return <>
        <TableRow>
            <TableCell>
                <Box className={classes.flexRow}>
                    {detail?.icon ? React.createElement(detail.icon) : null}
                    <Typography className={classes.nameText} component={"span"}>
                        {detail?.name}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
                {!(columnId in autoColDefs) ? <IconButton data-column={columnId} onClick={handleDeleteColumn}><Delete /></IconButton> : null}
                {detail && detail?.type === "singleSelect" && <IconButton onClick={() => setOpen(!open)}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>}
                {detail && <IconButton onClick={handleToggleHidden}>{hidden ? <VisibilityOff/> : <Visibility/>}</IconButton>}
                {detail && detail?.type === "string" && <IconButton className={wrap ? classes.wrap : ""} onClick={handleToggleTextWrap}><WrapText /></IconButton>}
            </TableCell>
        </TableRow>
        {detail && detail?.type === "singleSelect" &&
            <TableRow >
                <TableCell colSpan={2} sx={{ borderBottom: open ? undefined : 0 }}>
                    <Collapse in={open} timeout={150} unmountOnExit>
                        <Typography>Options</Typography>
                        <Form display={"flex"} >
                            <List dense disablePadding className={classes.list}>
                                {Object.entries(detail.valueOptions ?? {}).map(([id, option]) =>
                                    <ListItem key={id}>
                                        <TextFieldAuto
                                            variant={"outlined"}
                                            size={"small"}
                                            margin={"dense"}
                                            fullWidth
                                            defaultValue={option}
                                            onCommit={(value) => handleSetValueOption(id, value)} />
                                    </ListItem>)}
                            </List>
                        </Form>
                        <Divider />
                        <Form display={"flex"} >
                            <TextFieldAuto
                                label={"Add option"}
                                variant={"outlined"}
                                size={"small"}
                                margin={"dense"}
                                fullWidth
                                defaultValue={""}
                                onCommit={(value) => handleAddValueOption(value)} />
                            <IconButton onClick={handleAddValueOption} size={"small"}>
                                <Add />
                            </IconButton>
                        </Form>
                    </Collapse>
                </TableCell>
            </TableRow>}
    </>
}

interface Props
{
    user: PathwayUser
    project: string
    sheetId?: string
    constants: Constants
    modeData: ISheetModeData
    sheets: Sheets
}
function SheetInspector({ user, sheetId, project, constants, modeData, sheets }: Props)
{
    const classes = useStyles();
    const { set, remove } = useContext(FirebaseContext);
    const confirm = useContext(ConfirmContext);

    sheetId = sheetId ?? modeData.sheetId;
    const sheet = useMemo(() => sheetId && sheetId in sheets ? sheets[sheetId] : {} as Sheet, [sheetId, sheets]);

    const handleCommitSheetField = useCallback((field: string, value: string) =>
    {
        set(`sheets/${project}/${sheetId}/${field}`, value);
    }, [set, project, sheetId]);

    const handleDeleteColumn = (event: React.MouseEvent<HTMLElement>) =>
    {
        const columnId = event.currentTarget.dataset.column;
        if (columnId)
        {
            confirm(<Typography>Are you sure you want to remove this column?</Typography>).then(() =>
            {
                remove(`sheets/${project}/${sheetId}/columns/${columnId}`);
            }).catch(() => { })
        }
    }

    const handleDeleteSheet = () =>
    {
        confirm(<Typography>{`Are you sure you want to delete the entire sheet named ${sheet.name}?`}</Typography>).then(() =>
        {
            remove(`sheets/${project}/${sheetId}`).catch(console.error);
        }).catch(() => { })
    }

    const NameField = useCallback(() => <TextFieldAuto
        sx={{ m: 1 }}
        size={"small"}
        margin={"dense"}
        label="Name"
        fullWidth
        value={sheet.name}
        onCommit={(value) => value && handleCommitSheetField("name", value as string)} />, [sheet, handleCommitSheetField]);

    const DescriptionField = useCallback(() => <TextFieldAuto
        sx={{ m: 1 }}
        size={"small"}
        margin={"dense"}
        label="Description"
        fullWidth
        multiline
        rows={4}
        value={sheet.description ?? ""}
        onCommit={(value) => value && handleCommitSheetField("description", value as string)} />, [sheet, handleCommitSheetField]);

    return <Box className={classes.container}>
        <Form className={classes.flexColumn}>
            <Box className={classes.flexRow}>
                <NameField />
                <IconButton onClick={handleDeleteSheet} disabled={user.roles.admin === false}><Delete /></IconButton>
            </Box>
            <DescriptionField />
        </Form>
        <Divider />
        <Table size="small" padding={"none"}>
            <TableHead>
                <TableRow>
                    <TableCell>Column</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {Object.entries(sheet.columns ?? {}).filter(([id, column]) => !(id in autoColDefs)).sort(([idA, colA], [idB, colB]) => colA.order - colB.order).map((columnEntry) => 
                {
                    const [id] = columnEntry;
                    return <Row key={id} columnEntry={columnEntry} handleDeleteColumn={handleDeleteColumn} constants={constants} project={project} sheetId={sheetId} />
                })}
            </TableBody>
        </Table>
    </Box >
}

const mapStateToProps = (store: never) => ({
    user: selectUser(store),
    project: selectProject(store),
    constants: selectConstants(store),
    modeData: selectModeData(store) ?? {} as ISheetModeData,
    sheets: selectSheets(store) ?? {}
});
export default connect(mapStateToProps, {
})(SheetInspector);
